import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { Button } from '~/ui/buttons';
import LightButton from '~/editor/buttons/LightButton';
import { FaceRating, faceOptionsFor } from './FaceRating';
import { useGenerationFeedbackPrompt } from './useGenerationFeedbackPrompt';

export const GENERATION_FEEDBACK_FLAG = 'course_generation_feedback';
const COMMENT_MAX_LENGTH = 2000;
const COMMENT_MAX_HEIGHT = 160;
const SAVED_NOTICE_MS = 4000;
const TITLE_ID = 'course-generation-feedback-title';
const COMMENT_ID = 'course-generation-feedback-comment';

const Dock = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: #1f1f1f;
`;

const Panel = styled.section`
  width: 460px;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  padding: 24px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  background: var(--color-white);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
`;

const Eyebrow = styled.p`
  margin: 0 0 4px;
  font-size: 12px;
  line-height: 18px;
  color: var(--color-grey-dk);
`;

const Title = styled.h2`
  margin: 0;
  font-family: inherit;
  font-size: 18px;
  line-height: 27px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: -4px -4px 0 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: none;
  color: #444746;
  cursor: pointer;

  &:hover {
    background: var(--color-grey-lt);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-dk);
    outline-offset: 2px;
  }
`;

const DetailsRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

const VisuallyHidden = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Comment = styled.textarea`
  flex: 1;
  min-width: 0;
  min-height: 38px;
  max-height: ${COMMENT_MAX_HEIGHT}px;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid color-mix(in srgb, var(--color-grey-dk) 75%, white);
  border-radius: 8px;
  background: var(--color-white);
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  color: inherit;
  resize: none;
  overflow-y: auto;

  &::placeholder {
    color: var(--color-grey-dk);
  }

  &:focus {
    outline: 2px solid var(--color-accent-dk);
    outline-offset: 0;
    border-color: transparent;
  }
`;

const SubmitButton = styled(Button)`
  height: 38px;
  min-height: 38px;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 700;
`;

const SavedNotice = styled.p`
  margin: 0;
  padding: 8px 14px;
  border-radius: 10px;
  background: #e8f7ef;
  color: #1f7a45;
  font-size: 13px;
  font-weight: 600;
`;

const Launcher = styled(LightButton)`
  border-radius: 999px;
  box-shadow: 0 4px 14px rgba(151, 56, 255, 0.18);
`;

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M5 5l10 10M15 5L5 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const GenerationFeedback = () => {
  const { apiService, training, sequence, hasFeatureFlag } = useEditorStore();
  const prompt = useGenerationFeedbackPrompt({
    apiService,
    trainingId: training?.id,
    sequenceId: sequence?.id,
    enabled: hasFeatureFlag(GENERATION_FEEDBACK_FLAG)
  });
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [faceClicked, setFaceClicked] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!prompt.isOpen) return;

    const previous = prompt.latestFeedback;
    const sameScale = (previous?.ratingScale ?? 5) === prompt.ratingScale;
    setRating(previous && sameScale ? previous.rating : null);
    setComment('');
    setFaceClicked(false);
  }, [prompt.isOpen]);

  useEffect(() => {
    if (!justSaved) return;

    const timer = window.setTimeout(() => setJustSaved(false), SAVED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [justSaved]);

  useLayoutEffect(() => {
    const field = commentRef.current;
    if (!field) return;

    field.style.height = 'auto';
    field.style.height = `${Math.min(field.scrollHeight, COMMENT_MAX_HEIGHT)}px`;
  }, [comment, faceClicked]);

  if (!prompt.eligible) return null;

  const latestLabel = faceOptionsFor(
    prompt.latestFeedback?.ratingScale ?? 5
  ).find(option => option.value === prompt.latestFeedback?.rating)?.label;

  const send = async () => {
    if (!rating) return;

    setJustSaved(await prompt.submit({ rating, comment }));
  };

  return (
    <Dock>
      {justSaved && !prompt.isOpen && (
        <SavedNotice role="status">
          Thanks! Your feedback was saved.
        </SavedNotice>
      )}
      {prompt.isOpen ? (
        <Panel role="dialog" aria-labelledby={TITLE_ID}>
          <Header>
            <div>
              <Eyebrow>Help refine our product</Eyebrow>
              <Title id={TITLE_ID}>
                How satisfied are you with this generated course?
              </Title>
            </div>
            <CloseButton
              type="button"
              aria-label="Close feedback"
              onClick={prompt.dismiss}
            >
              <CloseIcon />
            </CloseButton>
          </Header>
          <FaceRating
            scale={prompt.ratingScale}
            value={rating}
            onChange={value => {
              setRating(value);
              setFaceClicked(true);
            }}
          />
          {faceClicked && (
            <DetailsRow>
              <VisuallyHidden htmlFor={COMMENT_ID}>
                Tell us more (optional)
              </VisuallyHidden>
              <Comment
                id={COMMENT_ID}
                ref={commentRef}
                rows={1}
                value={comment}
                maxLength={COMMENT_MAX_LENGTH}
                placeholder="Tell us more (optional)..."
                onChange={event => setComment(event.target.value)}
              />
              <SubmitButton
                type="button"
                onClick={send}
                disabled={!rating || prompt.isSubmitting}
              >
                Submit
              </SubmitButton>
            </DetailsRow>
          )}
        </Panel>
      ) : (
        <Launcher onClick={prompt.open} title={latestLabel}>
          {prompt.latestFeedback ? 'Update course rating' : 'Rate this course'}
        </Launcher>
      )}
    </Dock>
  );
};
